// @ts-nocheck
import { forwardRef } from 'react';
import {
  Box,
  Stack,
  Text,
  RingProgress,
  useMantineTheme,
  useComputedColorScheme,
} from '@mantine/core';
import { Paper } from '../../../ui';
import { FadeIn } from '../../../animations';
import type { GaugeProps } from '../types';
import { usePreferencesStore } from '../../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../../hooks/useAccessibilityStyles';

/**
 * CircularGauge - Circular progress gauge
 *
 * Displays progress as a ring/circle with automatic color coding based on thresholds.
 */
const CircularGauge = forwardRef<HTMLDivElement, GaugeProps>(
  (
    {
      title,
      value,
      max,
      min = 0,
      unit,
      description,
      thresholds,
      showPercentage = true,
      showValue = true,
      size = 'md',
      variant = 'default',
      color,
      animate = true,
      className,
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const colorScheme = useComputedColorScheme('light');
    const isDark = colorScheme === 'dark';
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const sizes = {
      xs: { ringSize: 80, thickness: 8, fontSize: 'sm', labelSize: 'xs' },
      sm: { ringSize: 100, thickness: 10, fontSize: 'md', labelSize: 'sm' },
      md: { ringSize: 140, thickness: 12, fontSize: 'lg', labelSize: 'sm' },
      lg: { ringSize: 180, thickness: 14, fontSize: 'xl', labelSize: 'md' },
      xl: { ringSize: 220, thickness: 16, fontSize: '2rem', labelSize: 'lg' },
    };

    const currentSize = sizes[size];

    // Calculate percentage
    const percentage = Math.min(Math.max(((value - min) / (max - min)) * 100, 0), 100);

    // Determine color based on thresholds or percentage
    const getGaugeColor = () => {
      if (color) return color;

      if (thresholds && thresholds.length > 0) {
        const sortedThresholds = [...thresholds].sort((a, b) => b.value - a.value);
        for (const threshold of sortedThresholds) {
          if (value >= threshold.value) {
            return threshold.color;
          }
        }
        return sortedThresholds[sortedThresholds.length - 1].color;
      }

      // Default color coding
      if (percentage >= 70) return 'green';
      if (percentage >= 30) return 'yellow';
      return 'red';
    };

    const gaugeColor = getGaugeColor();

    return (
      <FadeIn>
        <Paper ref={ref} variant={variant} p="lg" className={className}>
          <Stack gap="md" align="center">
            {title && (
              <Text size={currentSize.labelSize} fw={600} ta="center" style={accessibilityStyles}>
                {title}
              </Text>
            )}

            <Box style={{ position: 'relative' }}>
              <RingProgress
                size={currentSize.ringSize}
                thickness={currentSize.thickness}
                sections={[{ value: percentage, color: gaugeColor }]}
                label={
                  <Box style={{ textAlign: 'center' }}>
                    {showValue && (
                      <Text size={currentSize.fontSize} fw={700}>
                        {value}
                        {unit && <Text component="span" size="sm">{unit}</Text>}
                      </Text>
                    )}
                    {showPercentage && (
                      <Text size="xs" c="dimmed">
                        {percentage.toFixed(1)}%
                      </Text>
                    )}
                  </Box>
                }
              />
            </Box>

            {description && (
              <Text size="sm" c="dimmed" ta="center" style={accessibilityStyles}>
                {description}
              </Text>
            )}

            {thresholds && thresholds.length > 0 && (
              <Stack gap={4} style={{ width: '100%' }}>
                {thresholds.map((threshold, idx) => (
                  <Box
                    key={idx}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      fontSize: '12px',
                    }}
                  >
                    <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Box
                        style={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: threshold.color,
                        }}
                      />
                      <Text size="xs">{threshold.label || `Threshold ${idx + 1}`}</Text>
                    </Box>
                    <Text size="xs" c="dimmed">
                      ≥ {threshold.value}{unit}
                    </Text>
                  </Box>
                ))}
              </Stack>
            )}
          </Stack>
        </Paper>
      </FadeIn>
    );
  }
);

CircularGauge.displayName = 'CircularGauge';

export default CircularGauge;
